import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import styled from "styled-components/native";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import { VDBIcons } from "@utils/VDBIcons";
import { SpaceType } from "@components/atoms/Spacer/types";
import { Button, Spacer, VDBText } from "@components/atoms";
import { flattenAndSort } from "../utils";
import LineViewContainer from "@scenes/requests/utils/LineView";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import { useLinkTo } from "@react-navigation/native";
import { RedirectFunc } from "./utils";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import { ProductCategory } from "@utils/constants";
import { RequestTypes } from "@scenes/requests/utils/utils";
import React from "react";
import { renderDateTime } from "@scenes/requests/utils/RequestsScreen";
import ShippingInformation from "./ShippingInformation";
import { useOrderCart } from "@redux/slices/globalSlice";
import { useQueryClient } from "@tanstack/react-query";

type SuccessPopUpProps = {
  successData?: any;
  successPopUpConfig?: any;
  deepLink?: string;
  productType?: string;
  priceMode?: number;
  onClose?: () => void;
};

const SuccessPopUp = ({
  successData,
  successPopUpConfig = {},
  deepLink,
  productType,
  priceMode,
  onClose,
}: SuccessPopUpProps) => {
  const { theme, typography, t } = useScreenOptionsHooks();
  const sortedConfig = flattenAndSort(successPopUpConfig);
  const linkTo = useLinkTo();
  const queryClient = useQueryClient();
  const orderCart = useOrderCart();
  const shippingInformation = orderCart?.shipping_information;
  const showShippingInformation =
    orderCart?.shipping_information?.price_modes?.includes(priceMode);

  const RenderInfoRows = () => {
    if (!successData) return null;
    const getValueFromPath = (obj: any, path: string) => {
      return path.split(".").reduce((acc, key) => acc?.[key], obj);
    };
    const formatKey = (key: string) =>
      key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

    return (
      <>
        {sortedConfig.map(({ key, label, path }) => {
          const value = getValueFromPath(successData, path);
          if (!value) return null;
          return (
            <InfoRow key={`${key}`}>
              <Label>
                <VDBText color={theme["core-06"]} type={typography["Minor-R"]}>
                  {t(label) || formatKey(key)}:
                </VDBText>
              </Label>
              <Spacer size={SpaceType.ExtraSmall} />
              <Value>
                <VDBText color={theme["core-05"]} type={typography["Minor-M"]}>
                  {key === "created_at"
                    ? renderDateTime(new Date(), "DD MMM, y [at] hh:mm A")
                    : value || "-"}
                </VDBText>
              </Value>
            </InfoRow>
          );
        })}
      </>
    );
  };

  const navigateToOrder = () => {
    const navigateParams = {
      selectedTab: "sent",
      initial: false,
      screen: Routes.RequestDetailScreen,
      params: {
        id: successData?.id,
        selectedTab: "sent",
        requestName: RequestTypes.Order,
        productType: productType ?? ProductCategory.JEWELRY,
        price_mode: priceMode,
      },
    };
    onClose?.();
    queryClient.invalidateQueries({ queryKey: ["stock_item_request"] });
    queryClient.invalidateQueries({ queryKey: ["GET_UNREAD_REQUEST"] });
    if (theme.isMobile) {
      navigate(Routes.GlobalNavigator, navigateParams);
    } else {
      navigate(Routes.AppNavigator, {
        screen: Routes.RequestHistoryNavigator,
        params: navigateParams,
      });
    }
  };

  return (
    <ParentContainer>
      <MessageIconContainer>
        <VDBIcon
          color={theme["success-01"]}
          size={40}
          variant={IconsVariant.VDBCustom}
          name={VDBIcons.CHECKMARK}
        />
        <Spacer size={SpaceType.Basic} />
        <VDBText color={theme["success-01"]} type={typography["H2-R"]}>
          {t("orderCart.successPopUp.message.title.text")}
        </VDBText>
        <Spacer size={SpaceType.ExtraSmall} />
        <VDBText
          color={theme["core-06"]}
          type={typography["Body-R"]}
          textAlign="center"
        >
          {t("orderCart.successPopUp.message.description.text").replace(
            "{{x}}",
            ""
          )}
          <VDBText color={theme["core-05"]} type={typography["Body-R"]}>
            {successData?.buyer?.email}
          </VDBText>
        </VDBText>
        <Spacer size={SpaceType.Big} />
      </MessageIconContainer>
      <CenterContainer>
        <InfoContainer>
          <RenderInfoRows />
        </InfoContainer>
      </CenterContainer>
      {showShippingInformation && successData?.shipping_instructions && (
        <>
          <Spacer size={SpaceType.Basic} />
          <ShippingInformation
            title={shippingInformation?.title}
            description={successData?.shipping_instructions}
            descriptionHeight={shippingInformation?.description_height}
            icon={shippingInformation?.icon}
          />
        </>
      )}
      <Spacer size={SpaceType.Basic} />
      <CenterContainer>
        <ContanctInfoContainer>
          <TextContainer>
            <VDBText color={theme["core-05"]} type={typography["H3-M"]}>
              {t("orderCart.successPopUp.contactInfo.header.text")}
            </VDBText>
            <Spacer size={SpaceType.ExtraSmall} />
            <VDBText color={theme["core-05"]} type={typography["H3-M"]}>
              {t("orderCart.successPopUp.contactInfo.headerText.text")}
            </VDBText>
          </TextContainer>
          <LineViewContainer background={"core-03"} />
          <InfoContainer>
            <InfoRow>
              <Label>
                <VDBText color={theme["core-06"]} type={typography["Minor-R"]}>
                  {t("orderCart.successPopUp.contactInfo.email.text")}:
                </VDBText>
              </Label>
              <Spacer size={SpaceType.ExtraSmall} />
              <Value>
                <VDBText
                  color={theme["link-static"]}
                  type={typography["Minor-M"]}
                >
                  {successData?.seller?.contact_email ?? "-"}
                </VDBText>
              </Value>
            </InfoRow>
            <InfoRow>
              <Label>
                <VDBText color={theme["core-06"]} type={typography["Minor-R"]}>
                  {t("orderCart.successPopUp.contactInfo.number.text")}:
                </VDBText>
              </Label>
              <Spacer size={SpaceType.ExtraSmall} />
              <Value>
                <VDBText
                  color={theme["link-static"]}
                  type={typography["Minor-M"]}
                >
                  {successData?.seller?.mobile_phone ?? "-"}
                </VDBText>
              </Value>
            </InfoRow>
            <InfoRow>
              <Label>
                <VDBText color={theme["core-06"]} type={typography["Minor-R"]}>
                  {t("orderCart.successPopUp.contactInfo.website.text")}:
                </VDBText>
              </Label>
              <Spacer size={SpaceType.ExtraSmall} />
              <Value>
                <VDBText
                  color={theme["link-static"]}
                  type={typography["Minor-M"]}
                >
                  {successData?.seller?.website
                    ? successData?.seller?.website
                    : "-"}
                </VDBText>
              </Value>
            </InfoRow>
          </InfoContainer>
        </ContanctInfoContainer>
      </CenterContainer>
      <Spacer size={SpaceType.ExtraLarge} />
      <ButtonContainer>
        <Button
          category={ButtonCategory.default}
          type={ButtonType.primary}
          title={t("orderCart.successPopUp.button.primary.text")}
          onPress={() => {
            onClose?.();
            deepLink
              ? RedirectFunc(deepLink, linkTo)
              : navigate(Routes.ExploreNavigator, {
                  screen: Routes.Explore,
                  initial: false,
                });
          }}
          maxWidth={theme.isLargeScreen ? 343 : 608}
        />
        <Spacer size={SpaceType.Double} />
        <Button
          category={ButtonCategory.default}
          type={ButtonType.secondary}
          title={t("orderCart.successPopUp.button.secondary.text")}
          onPress={navigateToOrder}
          maxWidth={theme.isLargeScreen ? 343 : 608}
        />
      </ButtonContainer>
    </ParentContainer>
  );
};

export default SuccessPopUp;

const ParentContainer = styled.View`
  padding-horizontal: ${SpaceType.Double}px;
  padding-top: ${SpaceType.Double}px;
  padding-bottom: ${SpaceType.ExtraLarge}px;
  background-color: ${({ theme }) => theme["core-02"]};
`;
const MessageIconContainer = styled.View`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  ${({ theme }) => theme.isTablet && `max-width: 608px;`}
  width: 100%;
  align-self: center;
`;

const InfoContainer = styled.View`
  background-color: ${({ theme }) => theme["core-01"]};
  padding: ${SpaceType.Basic}px;
  border-radius: ${SpaceType.Basic}px;
  ${({ theme }) => theme.isTablet && `max-width: 608px;`}
  width: 100%;
  gap: ${SpaceType.Basic}px;
`;

const ContanctInfoContainer = styled.View`
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: ${SpaceType.Basic}px;
  ${({ theme }) => theme.isTablet && `max-width: 608px;`}
  width: 100%;
`;

const InfoRow = styled.View`
  flex-direction: row;
`;

const Label = styled.View`
  width: 112px;
`;

const Value = styled.View`
  flex: 1;
`;
const TextContainer = styled.View`
  padding-horizontal: ${SpaceType.Basic}px;
  padding-vertical: ${SpaceType.Extra}px;
  flex-direction: column;
`;
const ButtonContainer = styled.View`
  justify-content: center;
  align-items: center;
`;

const CenterContainer = styled.View`
  justify-content: center;
  align-items: center;
  width: 100%;
`;
